import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IoArrowBackOutline,
  IoBookOutline,
  IoCalendarOutline,
  IoCameraOutline,
  IoCheckmarkCircleOutline,
  IoChevronForwardOutline,
  IoHelpCircleOutline,
  IoLinkOutline,
  IoMapOutline,
  IoRocketOutline,
} from "react-icons/io5";
import SeoMeta from "../seo/SeoMeta";
import { LEARN_OG_IMAGE, isPublicLearnPathname, resolveLearnPathForMode } from "../../utils/learnRoutes";

const XIMA_PLAY_URL = "https://juegoxima.com/";

export const buildStructuredData = (page) => {
  if (!page) return null;

  const primarySchema =
    page.structuredDataType === "HowTo" && Array.isArray(page.steps) && page.steps.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: page.h1,
          headline: page.h1,
          description: page.metaDescription,
          url: page.canonicalUrl,
          image: page.ogImage || LEARN_OG_IMAGE,
          datePublished: page.publishedAt || page.updatedAt,
          dateModified: page.updatedAt,
          publisher: {
            "@type": "Organization",
            name: "XIMA",
            url: "https://juegoxima.com",
          },
          step: page.steps.map((step, index) => ({
            "@type": "HowToStep",
            position: index + 1,
            name: `Paso ${index + 1}`,
            text: step,
          })),
        }
      : {
    "@context": "https://schema.org",
    "@type": page.structuredDataType || "Article",
    headline: page.h1,
    name: page.h1,
    description: page.metaDescription,
    url: page.canonicalUrl,
    image: page.ogImage || LEARN_OG_IMAGE,
    datePublished: page.publishedAt || page.updatedAt,
    dateModified: page.updatedAt,
    mainEntityOfPage: page.canonicalUrl,
    publisher: {
      "@type": "Organization",
      name: "XIMA",
      url: "https://juegoxima.com",
    },
        };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: page.breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.label,
      item: crumb.path?.startsWith("http")
        ? crumb.path
        : `https://juegoxima.com${String(crumb.path || "").replace(/^\/game\/learn/, "/aprender")}`,
    })),
  };

  const faq =
    page.faqs?.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: page.faqs.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null;

  return faq ? [primarySchema, breadcrumb, faq] : [primarySchema, breadcrumb];
};

const getSectionByHeading = (page, patterns) =>
  (page?.sections || []).find((section) =>
    patterns.some((pattern) => pattern.test(String(section?.heading || "")))
  );

const getPrimaryInterfaceLabel = (page) => {
  const slug = String(page?.slug || "");
  const labelBySlug = [
    [/^ciencia$/, "Modo Ciencia"],
    [/^azar$/, "Modo Azar"],
    [/^cienzar$/, "Modo Cienzar"],
    [/^modos\/ciencia-vs-azar$/, "Ciencia V/S Azar"],
    [/^modos\/freebox$/, "FreeBox"],
    [/^modos-de-juego$/, "Modos de juego"],
    [/^reglas/, "Reglas de XIMA"],
    [/^ver-partidos/, "Ver partidos"],
    [/^perfil\/|^tutoriales\/editar-perfil/, "Perfil"],
    [/^amigos|^tutoriales\/.*amistad|^tutoriales\/buscar-jugador|^tutoriales\/unirse-partida-amigo/, "Amigos"],
    [/^tienda/, "Tienda"],
    [/^ranking/, "Ranking"],
    [/^estadisticas/, "Estadisticas"],
    [/^replay|^historial|^tutoriales\/ver-replay/, "Historial y replay"],
    [/^torneos|^tutoriales\/.*torneo/, "Torneos"],
    [/^puzzles|^tutoriales\/.*puzzle/, "Puzzles"],
    [/^bots|^tutoriales\/jugar-con-bot/, "Jugar con bot"],
    [/^ajustes|^idiomas|^control-por-voz/, "Ajustes"],
    [/^chat-de-voz/, "Partida compatible con chat de voz"],
    [/^noticias/, "Noticias"],
    [/^novedades/, "Novedades"],
    [/^lobby|^interfaz/, "Lobby"],
    [/^partidas|^tutoriales\/crear-partida|^tutoriales\/unirse-partida|^tutoriales\/buscar-partidas/, "Partidas"],
  ];
  const mappedLabel = labelBySlug.find(([pattern]) => pattern.test(slug))?.[1];
  if (mappedLabel) return mappedLabel;

  return page?.h1 || "la interfaz relacionada";
};

const cleanStepText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\.$/, "");

export const buildFallbackSteps = (page) => {
  if (!page) return [];

  const interfaceLabel = getPrimaryInterfaceLabel(page);
  const locationSection = getSectionByHeading(page, [/donde aparece/i, /ubicacion/i, /acceso/i, /entrada/i]);
  const behaviorSection = getSectionByHeading(page, [
    /como funciona/i,
    /dato visible/i,
    /funcion/i,
    /estado/i,
    /antes de empezar/i,
    /objetivo/i,
  ]);
  const usageSection = getSectionByHeading(page, [/como usar/i, /uso/i, /pasos/i, /flujo/i]);
  const cautionSection = getSectionByHeading(page, [/privacidad/i, /seguridad/i, /recomendacion/i, /cuidado/i]);
  const firstExample = (page.examples || []).find(Boolean);
  const nextAction = page.cta?.label || page.internalLinks?.find((link) => link?.label)?.label;

  const steps = [];

  if (locationSection?.body) {
    steps.push(`Localiza ${interfaceLabel} en la interfaz: ${cleanStepText(locationSection.body)}.`);
  } else if (behaviorSection?.body) {
    steps.push(`${interfaceLabel}: ${cleanStepText(behaviorSection.body)}.`);
  } else if (page.intro) {
    steps.push(`Ten en cuenta: ${cleanStepText(page.intro)}.`);
  } else {
    steps.push(`Revisa ${interfaceLabel} dentro de XIMA cuando necesites esta funcion.`);
  }

  if (behaviorSection?.body && steps[0] !== `${interfaceLabel}: ${cleanStepText(behaviorSection.body)}.`) {
    steps.push(`Revisa como funciona ${interfaceLabel}: ${cleanStepText(behaviorSection.body)}.`);
  } else if (page.intro) {
    steps.push(`Identifica en la interfaz lo que explica esta guia: ${cleanStepText(page.intro)}.`);
  }

  if (usageSection?.body && usageSection !== behaviorSection) {
    steps.push(`Usa ${interfaceLabel} asi: ${cleanStepText(usageSection.body)}.`);
  } else if (page.metaDescription) {
    steps.push(`Usa ${interfaceLabel} con este objetivo: ${cleanStepText(page.metaDescription)}.`);
  }

  if (firstExample) {
    steps.push(`Ten en cuenta este caso visible: ${cleanStepText(firstExample)}.`);
  }

  if (cautionSection?.body) {
    steps.push(`Antes de actuar, considera: ${cleanStepText(cautionSection.body)}.`);
  }

  if (nextAction) {
    steps.push(`Consulta "${nextAction}" si necesitas comparar o ampliar el contexto relacionado.`);
  }

  return Array.from(new Set(steps.filter(Boolean))).slice(0, 5);
};

const LearnSeoPage = ({ page, publicMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const structuredData = useMemo(() => buildStructuredData(page), [page]);
  const isPublicRoute = publicMode || isPublicLearnPathname(location.pathname);

  if (!page) return null;

  const goTo = (path) => {
    if (!path) return;
    if (/^https?:\/\//i.test(path)) {
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    const targetPath = resolveLearnPathForMode(path, isPublicRoute);
    if (targetPath.startsWith("/#")) {
      window.location.assign(targetPath);
      return;
    }
    navigate(targetPath);
  };
  const hrefFor = (path) => resolveLearnPathForMode(path, isPublicRoute);
  const handleAnchorNavigation = (event, path) => {
    event.preventDefault();
    goTo(path);
  };

  const pageSteps = Array.isArray(page.steps) ? page.steps : [];
  const displayedSteps = pageSteps.length > 0 ? pageSteps : buildFallbackSteps(page);
  const hasVerifiedPageSteps = pageSteps.length > 0;
  const stepsTitle = page.stepsTitle || (hasVerifiedPageSteps ? "Pasos" : "Datos utiles");

  return (
    <div className="xima-learn-screen">
      <SeoMeta
        title={page.title}
        description={page.metaDescription}
        canonicalUrl={page.canonicalUrl}
        ogTitle={page.ogTitle}
        ogDescription={page.ogDescription}
        ogImage={page.ogImage || LEARN_OG_IMAGE}
        robots={page.robots || "index,follow"}
        datePublished={page.publishedAt || page.updatedAt}
        dateModified={page.updatedAt}
        structuredData={structuredData}
      />

      <div className="xima-learn-shell">
        <nav className="xima-learn-seo-breadcrumbs" aria-label="Breadcrumb">
          {page.breadcrumbs.map((crumb, index) => {
            const isLast = index === page.breadcrumbs.length - 1;
            return (
              <React.Fragment key={`${crumb.label}-${index}`}>
                {isLast ? (
                  <span aria-current="page">{crumb.label}</span>
                ) : (
                  <a href={hrefFor(crumb.path)} onClick={(event) => handleAnchorNavigation(event, crumb.path)}>
                    {crumb.label}
                  </a>
                )}
                {!isLast ? <IoChevronForwardOutline aria-hidden="true" /> : null}
              </React.Fragment>
            );
          })}
        </nav>

        <section className="xima-learn-hero xima-learn-seo-hero">
          <div className="xima-learn-hero-copy">
            <span className="xima-learn-title-icon">
              <IoBookOutline aria-hidden="true" />
            </span>
            <div>
              <span className="xima-learn-kicker">Guia XIMA</span>
              <h1>{page.h1}</h1>
              <p>{page.intro}</p>
            </div>
          </div>
          <div className="xima-learn-hero-actions">
            <a
              href={hrefFor("/game/learn")}
              onClick={(event) => handleAnchorNavigation(event, "/game/learn")}
              className="xima-learn-outline-button"
            >
              <IoArrowBackOutline aria-hidden="true" />
              <span>Aprender</span>
            </a>
            {isPublicRoute ? (
              <a href={XIMA_PLAY_URL} className="xima-learn-play-button" aria-label="Jugar XIMA">
                <IoRocketOutline aria-hidden="true" />
                <span>JUGAR</span>
              </a>
            ) : null}
            {page.cta ? (
              <a
                href={hrefFor(page.cta.path)}
                onClick={(event) => handleAnchorNavigation(event, page.cta.path)}
                className="xima-learn-green-button"
              >
                <IoRocketOutline aria-hidden="true" />
                <span>{page.cta.label}</span>
              </a>
            ) : null}
          </div>
          <div className="xima-learn-seo-meta-card">
            <span>
              <IoMapOutline aria-hidden="true" />
              {page.friendlyUrl}
            </span>
            <span>
              <IoCalendarOutline aria-hidden="true" />
              Actualizado: {page.updatedAt}
            </span>
          </div>
        </section>

        <div className="xima-learn-seo-layout">
          <article className="xima-learn-panel xima-learn-seo-article">
            <section>
              <h2>Informacion principal</h2>
              <div className="xima-learn-seo-sections">
                {page.sections.map((section) => (
                  <div key={section.heading} className="xima-learn-seo-section-block">
                    <h3>{section.heading}</h3>
                    <p>{section.body}</p>
                  </div>
                ))}
              </div>
            </section>

            {displayedSteps.length > 0 ? (
              <section>
                <h2>{stepsTitle}</h2>
                {page.stepsNote ? (
                  <p className="xima-learn-seo-step-note">{page.stepsNote}</p>
                ) : null}
                <ol className="xima-learn-seo-step-list">
                  {displayedSteps.map((step, index) => (
                    <li key={`${step}-${index}`}>
                      <span>{index + 1}</span>
                      <p>{step}</p>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}

            {page.examples.length > 0 ? (
              <section>
                <h2>Ejemplos</h2>
                <div className="xima-learn-seo-example-grid">
                  {page.examples.map((example, index) => (
                    <div key={`${example}-${index}`} className="xima-learn-seo-example">
                      <IoCheckmarkCircleOutline aria-hidden="true" />
                      <p>{example}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {page.screenshots.length > 0 ? (
              <section>
                <h2>Imagenes de referencia</h2>
                <div className="xima-learn-seo-screenshot-grid">
                  {page.screenshots.map((caption, index) => (
                    <figure key={`${caption}-${index}`} className="xima-learn-seo-screenshot">
                      <div>
                        <IoCameraOutline aria-hidden="true" />
                      </div>
                      <figcaption>{caption}</figcaption>
                    </figure>
                  ))}
                </div>
              </section>
            ) : null}

            {page.faqs.length > 0 ? (
              <section>
                <h2>Preguntas frecuentes</h2>
                <div className="xima-learn-seo-faq-list">
                  {page.faqs.map((faq) => (
                    <details key={faq.question}>
                      <summary>
                        <IoHelpCircleOutline aria-hidden="true" />
                        <span>{faq.question}</span>
                      </summary>
                      <p>{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            ) : null}
          </article>

          <aside className="xima-learn-book-aside xima-learn-seo-aside">
            <div className="xima-learn-aside-title">
              <IoLinkOutline aria-hidden="true" />
              <span>Enlaces internos</span>
            </div>
            <div className="xima-learn-seo-link-list">
              {page.internalLinks.map((link) => (
                <a
                  key={`${link.label}-${link.path}`}
                  href={hrefFor(link.path)}
                  onClick={(event) => handleAnchorNavigation(event, link.path)}
                >
                  <span>{link.label}</span>
                  <IoChevronForwardOutline aria-hidden="true" />
                </a>
              ))}
            </div>
            {page.cta ? (
              <div className="xima-learn-seo-cta">
                <strong>Siguiente paso</strong>
                <p>Continua aprendiendo con una guia relacionada.</p>
                <a
                  href={hrefFor(page.cta.path)}
                  onClick={(event) => handleAnchorNavigation(event, page.cta.path)}
                  className="xima-learn-green-button"
                >
                  {page.cta.label}
                </a>
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default LearnSeoPage;
